import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import logger from '../../../utils/logger';
import ApiService from '../../../services/api';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    minWidth: '500px',
    maxWidth: '600px'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: '20px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const UploadBox = styled(Box)(({ theme, isDragging }) => ({
  border: `2px dashed ${isDragging ? '#667eea' : '#ddd'}`,
  borderRadius: '12px',
  padding: '40px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: isDragging ? '#f5f7ff' : '#fafafa',
  '&:hover': {
    borderColor: '#667eea',
    backgroundColor: '#f5f7ff'
  }
}));

const VideoPreview = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  padding: '16px',
  backgroundColor: '#f5f5f5',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
}));

const VideoUploadModal = ({ open, onClose, accountId, locationId, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only MP4, MPEG, MOV, AVI, and WMV videos are allowed');
      return;
    }

    // Validate file size (max 100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Video size must be less than 100MB');
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a video file');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      logger.log('[VideoUpload] Starting video upload to GMB...');

      // Get Google OAuth token
      const googleAccessToken = localStorage.getItem('googleAccessToken');

      if (!googleAccessToken) {
        throw new Error('Google access token not found. Please sign in with Google.');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('video', selectedFile);
      formData.append('accountId', accountId);
      formData.append('locationId', locationId);
      formData.append('category', 'ADDITIONAL');

      logger.log('[VideoUpload] Uploading with accountId:', accountId, 'locationId:', locationId);

      // Simulate progress (since we can't track actual upload progress with current setup)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      // Upload to backend
      const result = await ApiService.request('/gmb/upload-video', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${googleAccessToken}`
        },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!result || !result.success || !result.data || !result.data.mediaItem) {
        throw new Error(result?.message || 'Failed to upload video');
      }

      logger.log('[VideoUpload] Video uploaded successfully:', result.data.mediaItem);
      setSuccess(true);

      // Notify parent component
      if (onSuccess) {
        onSuccess(result.data.mediaItem);
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      logger.error('[VideoUpload] Error uploading video:', err);
      setError(err.message || 'Failed to upload video. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setError('');
      setSuccess(false);
      setUploadProgress(0);
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideoFileIcon />
          <Typography variant="h6">Upload Video to GMB</Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={uploading}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Video uploaded successfully to GMB!
          </Alert>
        )}

        {!selectedFile ? (
          <UploadBox
            isDragging={isDragging}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('video-file-input').click()}
          >
            <CloudUploadIcon sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Drag & Drop Video Here
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              or click to browse
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
              Supported formats: MP4, MPEG, MOV, AVI, WMV
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Maximum size: 100MB
            </Typography>
            <input
              id="video-file-input"
              type="file"
              accept="video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/x-ms-wmv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </UploadBox>
        ) : (
          <VideoPreview>
            <VideoFileIcon sx={{ fontSize: 48, color: '#667eea' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatFileSize(selectedFile.size)}
              </Typography>
            </Box>
            {!uploading && !success && (
              <IconButton
                size="small"
                onClick={() => setSelectedFile(null)}
                sx={{ color: 'error.main' }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </VideoPreview>
        )}

        {uploading && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || uploading || success}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)'
            }
          }}
        >
          {uploading ? 'Uploading...' : 'Upload Video'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default VideoUploadModal;
